import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DevicePhoneMobileIcon,
  PuzzlePieceIcon,
  HeartIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  ArrowTopRightOnSquareIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';

const Home = () => {
  return (
    <>
      <Helmet>
        <title>AlonForge Solutions | Forge Your Digital Alon</title>
        <meta
          name="description"
          content="Crafting modern IT systems and vibrant UI portfolios for Filipino freelancers & small businesses—from quick ₱2,500 sites to scalable CRMs starting at ₱10,000"
        />
      </Helmet>

      {/* Hero Section */}
      <motion.section
        className="bg-gradient-to-br from-navy to-skyblue h-screen relative flex items-center justify-center overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Animated Wave Background */}
        <motion.svg
          className="absolute inset-0 opacity-20"
          viewBox="0 0 1440 320"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.path
            d="M0,96Q96,0 192,96T384,96T576,96T768,96T960,96T1152,96T1344,96L1440,96L1440,320L1344,320L1152,320L960,320L768,320L576,320L384,320L192,320L96,320L0,320Z"
            stroke="#F1F5F9"
            strokeWidth="2"
            fill="none"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.3 }}
            transition={{ duration: 3, repeat: Infinity, repeatType: 'reverse' }}
          />
        </motion.svg>

        {/* Hero Content */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-white text-center z-10 px-4 max-w-4xl mx-auto"
        >
          <h1 className="text-5xl md:text-7xl font-bold mb-6 font-heading">
            Forge Your Digital Alon
          </h1>
          <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto leading-relaxed">
            AlonForge Solutions: Crafting modern IT systems and vibrant UI
            portfolios for Filipino freelancers & small businesses—from quick
            ₱2,500 sites to scalable CRMs starting at ₱10,000.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/catalog"
              className="bg-white text-navy px-8 py-4 rounded-lg font-bold hover:bg-slate transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Build Your Portfolio
            </Link>
            <Link
              to="/systems"
              className="bg-skyblue text-white px-8 py-4 rounded-lg font-bold hover:bg-blue-600 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Explore Systems
            </Link>
          </div>
        </motion.div>
      </motion.section>

      {/* Stats Section */}
      <motion.section
        className="py-20 bg-gradient-to-br from-slate via-white to-slate"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { number: '50+', label: 'Portfolios Forged' },
              { number: '10+', label: 'Custom Systems' },
              { number: '100%', label: 'Pinoy Satisfaction' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center bg-gradient-to-br from-white to-slate rounded-xl p-8 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <div className="text-4xl font-bold text-navy mb-2 font-heading">
                  {stat.number}
                </div>
                <div className="text-gray-600 text-lg">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* SMM Portfolios Section - 50% */}
      <motion.section
        className="py-20 bg-gradient-to-br from-navy via-blue-700 to-skyblue relative overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-navy/20 to-skyblue/20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 font-heading">
              SMM Portfolio Showcases
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Stunning portfolios designed for social media managers to showcase their work and attract clients
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Creative SMM Portfolio',
                description: 'Modern design with Instagram feed integration and case study showcases',
                demo: 'https://example.com/smm-portfolio-1',
                image: '📱',
              },
              {
                title: 'Agency-Style Portfolio',
                description: 'Professional layout perfect for SMM agencies with client testimonials',
                demo: 'https://example.com/smm-portfolio-2',
                image: '🎨',
              },
              {
                title: 'Freelancer Portfolio',
                description: 'Clean, minimalist design highlighting your best social media campaigns',
                demo: 'https://example.com/smm-portfolio-3',
                image: '✨',
              },
            ].map((portfolio, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 hover:scale-105 border border-white/20"
              >
                <div className="text-5xl mb-4">{portfolio.image}</div>
                <h3 className="text-2xl font-bold text-white mb-3 font-heading">
                  {portfolio.title}
                </h3>
                <p className="text-blue-100 mb-6">{portfolio.description}</p>
                <a
                  href={portfolio.demo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-navy px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-all duration-300 transform hover:scale-105"
                >
                  View Demo
                  <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                </a>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center mt-12"
          >
            <Link
              to="/catalog"
              className="inline-block bg-white text-navy px-8 py-4 rounded-lg font-bold hover:bg-blue-50 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Browse All Portfolios
            </Link>
          </motion.div>
        </div>
      </motion.section>

      {/* Systems Section - 50% */}
      <motion.section
        className="py-20 bg-gradient-to-br from-slate via-white to-blue-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-navy mb-4 font-heading">
              Powerful IT Systems
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Custom-built systems to streamline your business operations and boost productivity
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'SMM Analytics Dashboard',
                description: 'Track social media performance, engagement metrics, and campaign ROI in real-time',
                price: 'From ₱10,000',
                icon: ChartBarIcon,
                features: ['Real-time Analytics', 'Multi-platform Integration', 'Custom Reports'],
              },
              {
                title: 'Content Management System',
                description: 'Streamline content creation, scheduling, and collaboration for your SMM team',
                price: 'From ₱12,000',
                icon: PresentationChartLineIcon,
                features: ['Content Calendar', 'Team Collaboration', 'Auto-scheduling'],
              },
              {
                title: 'Client CRM System',
                description: 'Manage clients, projects, and communications all in one place',
                price: 'From ₱15,000',
                icon: PuzzlePieceIcon,
                features: ['Client Database', 'Project Tracking', 'Invoice Management'],
              },
            ].map((system, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="bg-gradient-to-br from-white to-slate rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-skyblue"
              >
                <system.icon className="h-12 w-12 text-skyblue mb-4" />
                <h3 className="text-2xl font-bold text-navy mb-3 font-heading">
                  {system.title}
                </h3>
                <p className="text-gray-600 mb-4">{system.description}</p>
                <div className="mb-4">
                  <span className="bg-gradient-to-r from-navy to-skyblue text-white px-4 py-2 rounded-lg text-sm font-semibold">
                    {system.price}
                  </span>
                </div>
                <ul className="space-y-2 mb-6">
                  {system.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <CheckIcon className="h-5 w-5 text-skyblue mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/systems"
                  className="inline-block w-full text-center bg-gradient-to-r from-navy to-skyblue text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-800 hover:to-blue-600 transition-all duration-300"
                >
                  Learn More
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Pricing Section */}
      <motion.section
        className="py-20 bg-gradient-to-br from-navy via-blue-700 to-skyblue relative overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-navy/30 to-skyblue/30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 font-heading">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Choose the perfect plan for your needs. All prices are one-time payments.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Basic',
                price: '₱2,500',
                description: 'Perfect for getting started',
                features: [
                  '1 Static Page',
                  'Responsive Design',
                  'Contact Form',
                  'Mobile Optimized',
                  'Basic SEO',
                ],
                cta: 'Get Started',
                popular: false,
              },
              {
                name: 'Pro',
                price: '₱8,000',
                description: 'Ideal for growing businesses',
                features: [
                  'Multi-page Website',
                  'Content Management System',
                  'Custom Domain Setup',
                  'Advanced SEO',
                  'Analytics Integration',
                  'Social Media Integration',
                ],
                cta: 'Get Started',
                popular: true,
              },
              {
                name: 'Premium',
                price: '₱15,000',
                description: 'Complete solution with system',
                features: [
                  'Full Custom System',
                  'CMS + Admin Dashboard',
                  'Database Integration',
                  'API Integrations',
                  'Priority Support',
                  'Ongoing Maintenance',
                  'Custom Features',
                ],
                cta: 'Get Started',
                popular: false,
              },
            ].map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className={`relative bg-white/10 backdrop-blur-lg rounded-2xl p-8 hover:bg-white/20 transition-all duration-300 hover:scale-105 border-2 ${
                  plan.popular
                    ? 'border-white border-4 shadow-2xl'
                    : 'border-white/20'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2 font-heading">
                    {plan.name}
                  </h3>
                  <div className="text-5xl font-bold text-white mb-2 font-heading">
                    {plan.price}
                  </div>
                  <p className="text-blue-100 text-sm">{plan.description}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckIcon className="h-5 w-5 text-white mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-blue-100">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`block w-full text-center py-3 rounded-lg font-bold transition-all duration-300 transform hover:scale-105 ${
                    plan.popular
                      ? 'bg-white text-navy hover:bg-blue-50'
                      : 'bg-skyblue text-white hover:bg-blue-500'
                  }`}
                >
                  {plan.cta}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Features Section */}
      <motion.section
        className="py-20 bg-gradient-to-br from-white via-slate to-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-center mb-12 text-navy font-heading">
              Why Choose AlonForge?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-slate to-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100"
            >
              <DevicePhoneMobileIcon className="h-12 w-12 text-skyblue mb-4" />
              <h3 className="text-2xl font-semibold mb-4 font-heading">
                UI Portfolios: Responsive & Fast
              </h3>
              <p className="text-gray-600">
                Beautiful, mobile-first designs that showcase your work
                perfectly on any device. Lightning-fast load times.
              </p>
            </motion.div>

            {/* Feature 2 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gradient-to-br from-slate to-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100"
            >
              <PuzzlePieceIcon className="h-12 w-12 text-skyblue mb-4" />
              <h3 className="text-2xl font-semibold mb-4 font-heading">
                IT Systems: CRM, Inventory, Automations
              </h3>
              <p className="text-gray-600">
                Custom-built systems that scale with your business. From solo
                VA tools to team dashboards.
              </p>
            </motion.div>

            {/* Feature 3 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-gradient-to-br from-slate to-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100"
            >
              <HeartIcon className="h-12 w-12 text-skyblue mb-4" />
              <h3 className="text-2xl font-semibold mb-4 font-heading">
                Why Us: BSIS-Built, AI-Accelerated
              </h3>
              <p className="text-gray-600">
                Local support in Tagalog/English, understanding the Filipino
                freelancer hustle. Built by a BSIS graduate.
              </p>
            </motion.div>
          </div>
        </div>
      </motion.section>
    </>
  );
};

export default Home;
